<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <v-text-field
      class="mt-6"
      v-model="localValue.name"
      :rules="domainRules"
      :placeholder="$t('modals.addOutboundGateway.domainNamePlaceholder')"
      :label="localValue.name ? $t('modals.addOutboundGateway.domainNameLabel') : ''"
      :aria-label="$t('modals.addOutboundGateway.domainNameLabel')"
      variant="outlined"
    />
  </v-form>

  <div class="d-flex justify-space-between docs-link">
    <a class="enable-proxy-link d-flex align-center" :href="DOCS_REDIRECT_LINK" target="_blank">
      <v-icon icon="$help"></v-icon>
      <span class="coro-link">{{ $t("gatewaySettings.outbound.enableProxyLink") }}</span>
    </a>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";
import { Patterns } from "@coronet/cui";
import type { VuetifyFormRef } from "@/types";
import { useI18n } from "vue-i18n";
import type { EmailProxyDomain } from "@/_store/email-security/email-proxy.module.ts";

export default defineComponent({
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  emits: ["update:valid", "update:localValue", "dismissed"],
  setup(props, { emit }) {
    const DOCS_REDIRECT_LINK = "https://docs.coro.net/emailproxy/outbound/config/";
    const valid = ref(true);
    const form = ref<VuetifyFormRef>();

    const localValue = ref({
      name: props.config.item.name || "",
    });
    const i18n = useI18n();
    const validate = async () => {
      const validationResult = await form.value?.validate();

      return validationResult?.valid;
    };

    const domainRules = [
      (domain: string) => {
        if (!domain || domain.length <= 0) {
          return i18n.t("validations.required");
        }
        if (!Patterns.PUNYCODE_DOMAIN.test(domain.trim())) {
          return i18n.t("validations.punycodeDomain");
        }
        if (Patterns.NOT_ALLOWED_DOMAIN_ZONE.test(domain.trim())) {
          return i18n.t("validations.domainZoneIsNotAllowed");
        }
        const matchesExisting = props.config.item.existingDomains.some(
          (domainObj: EmailProxyDomain) =>
            domainObj.name === domain && domain !== props.config.item.name
        );
        if (matchesExisting) {
          return i18n.t("errors.domainAlreadyExists");
        }

        return true;
      },
    ];

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", {
          id: props.config.item.id,
          ...newVal,
        });
      },
      {
        deep: true,
        immediate: true,
      }
    );

    watch(
      valid,
      (newVal: boolean | null) => {
        if (newVal === null) {
          return emit("update:valid", true);
        }

        emit("update:valid", newVal);
      },
      { immediate: false }
    );

    return {
      validate,
      form,
      localValue,
      valid,
      domainRules,
      DOCS_REDIRECT_LINK,
    };
  },
});
</script>
<style lang="scss" scoped>
.v-chip {
  display: flex;
  justify-content: center;
  width: 50%;
  height: 160px !important;
  border-radius: 6px;
}

.v-chip .v-chip__content {
  width: 100% !important;
  text-align: center;
}

.v-chip-group {
  width: 100%;
}

:deep(*) {
  .v-chip-group .v-chip--selected {
    border: 2px solid rgb(var(--v-theme-primary)) !important;

    .v-chip__overlay {
      background-color: rgb(var(--v-theme-white));
    }
  }

  .v-alert__prepend {
    .icon:before {
      color: rgb(var(--v-theme-primary)) !important;
    }
  }
}

.deep-purple--text.text--accent-4 {
  color: transparent !important;
}

.v-chip:not(.v-chip--selected) {
  border: solid 1px rgb(var(--v-theme-indigo-pale)) !important;
}

.service-icon {
  height: 48px;
  width: 48px;
}

.services-chip-group {
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
  margin-top: 32px;
  width: 100%;
}

.docs-link {
  position: absolute;
  bottom: 33px;
}

.icon-help-xs::before {
  color: rgb(var(--v-theme-link));
}
</style>
